import { useAuthStore } from '@/stores/authStore';
import { displayDateTime, displayFull, displayTime } from './dateDisplay';
/**
 * The browser's IANA timezone, sent with any request whose result depends on
 * what "today" or a calendar day means.
 *
 * The backend stores timestamps as `timestamp with time zone` and runs its
 * connection in UTC, so without this it buckets days in UTC: an IST user's
 * "today" would begin at 05:30 local, and an evening record would count toward
 * the previous day. Every endpoint treats a missing or unrecognised zone as
 * UTC, so sending nothing is safe — it just restores the old behaviour.
 *
 * Mirrors backend/utils/timezones.py.
 */

/** e.g. "Asia/Kolkata". Empty string if the runtime cannot report one. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * Append `tz` to an endpoint path, picking `?` or `&` as needed.
 *
 *   withTz('/maintenance/stats')          -> '/maintenance/stats?tz=Asia%2FKolkata'
 *   withTz('/visitors/trend?days=7')      -> '/visitors/trend?days=7&tz=Asia%2FKolkata'
 */
export function withTz(url: string): string {
  const tz = browserTimeZone();
  if (!tz) return url;
  return `${url}${url.includes('?') ? '&' : '?'}tz=${encodeURIComponent(tz)}`;
}

// ---------------------------------------------------------------------------
// Display formatting — ONE implementation, and where the zone comes from
// ---------------------------------------------------------------------------
//
// `new Date(iso).toLocaleTimeString('en-IN', {...})` was copy-pasted into five
// files. 'en-IN' is a LOCALE, not a zone — it picks 12-hour clock and Indian
// digit grouping, and says nothing about which zone the instant is rendered in.
// With no `timeZone` option every one of those sites renders in the VIEWER's
// zone, which is the app-wide rule and is correct almost everywhere.
//
// It is NOT correct for a lobby screen or a door panel. Those belong to a
// building, not to whatever machine is driving them: a display in a Dubai lobby
// plugged into a laptop still set to Asia/Kolkata must show Dubai time, or it
// announces meetings at the wrong hour to everyone walking past. Those two
// screens pass an explicit `timeZone`; everything else keeps the default.
//
// The duplication itself caused this bug twice over, so the rule now lives in
// one place and the zone is a parameter rather than an omission.

export const DISPLAY_LOCALE = 'en-IN';

export type TimeValue = string | number | Date | null | undefined;

export interface TimeFormatOptions {
  /** IANA zone, e.g. "Asia/Dubai". Omit to use the viewer's own zone. */
  timeZone?: string;
  /** Include seconds. Off by default. */
  seconds?: boolean;
}

// 🔴 THESE THREE NOW DELEGATE. They were the app's second date renderer —
// correct about the ZONE, which is what they were written for, and silent
// about the PATTERN, which no renderer could express until utils/dateDisplay
// existed. A person who asked for YYYY-MM-DD got it everywhere except the two
// screens that call these, which is the worst of both.
//
// The signatures are unchanged, so the building-clock callers (lobby signage,
// room panel) keep passing their explicit zone and keep getting it.

/** "02:30 pm". In the organisation's zone unless `timeZone` is given. */
export function formatTime(value: TimeValue, o?: TimeFormatOptions): string {
  return displayTime(value, { timeZone: o?.timeZone });
}

/** "Monday, 21 August 2026" — the shape a screen whose whole content is the
 *  date wants, in the reader's own pattern. */
export function formatDate(value: TimeValue, o?: TimeFormatOptions): string {
  return displayFull(value, { timeZone: o?.timeZone });
}

/** "21/08/2026 02:30 pm". */
export function formatDateTime(value: TimeValue, o?: TimeFormatOptions): string {
  return displayDateTime(value, { timeZone: o?.timeZone });
}

/**
 * The zone abbreviation a viewer would recognise for `timeZone` — "GMT+4" or
 * "IST". Shown beside a time that is deliberately NOT in the viewer's own zone,
 * so nobody reads a building clock as their own.
 */
export function zoneLabel(timeZone?: string): string {
  if (!timeZone) return '';
  try {
    const parts = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
      timeZone, timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Wall clock -> instant, in a named zone
// ---------------------------------------------------------------------------
//
// The client-side past-window guard mirrors the server's _reject_if_ended so the
// common case needs no round trip. It was parsing `${date}T${time}` with the
// Date constructor, which reads a naive string in the BROWSER's zone — while the
// server decides in the ORGANISATION's. The server still has the final word, so
// the outcome was never wrong; but where the two zones differ the inline warning
// could contradict the answer the server was about to give, which is worse than
// no warning. The mirror now uses the same clock as the thing it mirrors.

/** Offset of `timeZone` from UTC, in milliseconds, AT a given instant. */
export function zoneOffsetMs(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at);
  const p: Record<string, string> = {};
  for (const part of parts) p[part.type] = part.value;
  // hour can format as "24" at midnight in some locales; %24 folds it to 0.
  const asIfUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour) % 24, Number(p.minute), Number(p.second),
  );
  return asIfUtc - at.getTime();
}

/**
 * The instant at which `wall` ("YYYY-MM-DDTHH:MM:SS") reads on the clock in
 * `timeZone`. With no zone this is the browser's, which is the app-wide rule.
 */
export function wallClockToInstant(wall: string, timeZone?: string): Date {
  if (!timeZone) return new Date(wall);
  const asUtc = new Date(`${wall}Z`);
  if (Number.isNaN(asUtc.getTime())) return asUtc;
  try {
    // Guess using the offset at the UTC reading, then refine once: within an
    // hour of a DST change the first guess can land the wrong side of the jump.
    const first = new Date(asUtc.getTime() - zoneOffsetMs(asUtc, timeZone));
    return new Date(asUtc.getTime() - zoneOffsetMs(first, timeZone));
  } catch {
    return new Date(wall);   // unknown zone: the viewer's, as everywhere else
  }
}

/**
 * The ORGANISATION's timezone, falling back to the browser's.
 *
 * 🔴 A DAY BOUNDARY DECIDES SOMETHING HERE. The pre-register form composed its
 * expected_arrival from the BROWSER's today with no offset, while the no-show
 * sweep judges "that day has passed" in the ORGANISATION's zone — so a host in
 * London could pre-register a Mumbai visitor for a date that looked correct on
 * screen and was already over in the building, and the sweep closed it as a
 * no-show within the hour.
 *
 * A visit happens at a building, so the building's day is the one that counts.
 * The browser is the fallback for an organisation that has set no zone, which
 * is the honest default when nobody has said otherwise.
 */
export function orgTimeZone(): string {
  const desk = deskZone;
  if (desk && desk.userId === useAuthStore.getState().user?.id) return desk.zone;
  const tz = useAuthStore.getState().user?.org_timezone;
  return (tz && tz.trim()) || browserTimeZone();
}

/**
 * 🔴 A FRONT DESK READS ITS BUILDING'S CLOCK (Reception guide walk, 3 Nov).
 * qa-sandbox's organisation is UTC and its building Asia/Kolkata: Approvals said
 * 06:00 pm for the visit its own alert called 23:30, and Mailroom logged a 12:57
 * parcel at 07:27. Some 25 desk renders took the renderer's default zone. The
 * desk's top bar, which only the reception shell mounts, says which building is
 * in front of the person; every other role never sets this and is unchanged.
 * Tied to the person so a sign-out in the same tab cannot pass it on.
 */
let deskZone: { userId: string; zone: string } | null = null;

export function setDeskTimeZone(zone: string | null | undefined): void {
  const userId = useAuthStore.getState().user?.id;
  deskZone = zone && zone.trim() && userId ? { userId, zone: zone.trim() } : null;
}

/**
 * MY building's timezone — the organisation's when no building names one.
 *
 * 🔴 THE OVERNIGHT WRONG DAY (Employee fix, final round). qa-sandbox's
 * organisation is UTC and its building Asia/Kolkata: from 00:00 to 05:30 at
 * the building the organisation's day is still yesterday, so at 01:00 IST on a
 * Monday My Day greeted "Sunday" and its visitors card asked for Sunday's
 * visitors. Every employee screen's "today / this week / tomorrow" is the day
 * where the person works (GET /auth/me day_timezone, from the server's
 * utils/booking_zone.person_zone). Organisation-wide staff analytics,
 * dashboards and reports stay on orgTimeZone() by design.
 */
export function myTimeZone(): string {
  const tz = useAuthStore.getState().user?.day_timezone;
  return (tz && tz.trim()) || orgTimeZone();
}

/** Today, as `yyyy-MM-dd`, at MY building (myTimeZone). */
export function myToday(): string {
  return todayIn(myTimeZone());
}

/**
 * The top-bar clock: "09:20" and a short name for the zone ("IST", "UTC", or
 * an offset like "GMT+5:30" where the locale has no abbreviation).
 *
 * 🔴 DSH-025: the caller passes orgTimeZone(). The clock followed the device
 * while every timestamp on the page used the organisation's zone, so the top
 * bar said 09:20 and the delivery log for that same minute said 03:50. An
 * unusable zone falls back to the device rather than breaking the header.
 */
export function clockReading(now: Date, timeZone: string): { time: string; zone: string } {
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
  };
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat(undefined, { ...opts, timeZone: timeZone || undefined })
      .formatToParts(now);
  } catch {
    parts = new Intl.DateTimeFormat(undefined, opts).formatToParts(now);
  }
  const time = parts
    .filter((p) => p.type === 'hour' || p.type === 'minute' || p.type === 'literal')
    .map((p) => p.value)
    .join('')
    .trim();
  return { time, zone: parts.find((p) => p.type === 'timeZoneName')?.value ?? '' };
}

/**
 * Today, as `yyyy-MM-dd`, in the organisation's zone.
 *
 * Built with Intl rather than by shifting a Date, because adding an offset to a
 * timestamp and reading its local parts is the arithmetic that produces the
 * off-by-one-day bugs this exists to prevent.
 */
export function orgToday(): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: orgTimeZone(),
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    // An unusable zone must not break a form. The browser's day is wrong less
    // often than a thrown exception is useful.
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}

/**
 * The display options for a time that belongs to a BUILDING: `{ timeZone }`
 * when the row says which zone, else nothing (the organisation's, as before).
 *
 * 🔴 A BOOKING IS SHOWN ON ITS BUILDING'S CLOCK (Employee fix, booking times).
 * qa-sandbox's organisation is UTC and its building Asia/Kolkata: formatted
 * on the organisation's clock the "Pilot team sync" read 04:30 in My bookings
 * while its notification — and the room's door — said 10:00. The API now
 * sends the building's zone with each booking and visit row (`timezone`,
 * `building_timezone`); every screen formats that row's times in it.
 */
export function inBuildingZone(timeZone?: string | null): { timeZone?: string } {
  const tz = timeZone?.trim();
  return tz ? { timeZone: tz } : {};
}

/** Today, as `yyyy-MM-dd`, in `timeZone` — the organisation's when none is
 *  given. For comparing a row's building-local `date` with "today" there. */
export function todayIn(timeZone?: string | null): string {
  const tz = timeZone?.trim();
  if (!tz) return orgToday();
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return orgToday();
  }
}

/**
 * When the NEXT booking is, said so a reader cannot mistake it for today:
 * "10:00 am" when it is today in `timeZone`, "Mon 19 Oct, 10:00 am" when not.
 *
 * 🔴 Deep-test SPC-023: the lobby screen printed only the time, so at 1 pm it
 * announced "Next booking at 10:00 am" for a meeting four days away. The day is
 * compared in the building's zone (the screen's), never the device's.
 */
export function formatNextOccurrence(value: TimeValue, o?: TimeFormatOptions & { now?: Date }): string {
  if (value === null || value === undefined || value === '') return '';
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  const zone = o?.timeZone || orgTimeZone();
  const fmt = (opts: Intl.DateTimeFormatOptions, locale: string) => {
    try {
      return new Intl.DateTimeFormat(locale, { ...opts, timeZone: zone });
    } catch {
      return new Intl.DateTimeFormat(locale, opts);
    }
  };
  const dayOf = fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA');
  const time = formatTime(at, { timeZone: o?.timeZone });
  if (dayOf.format(at) === dayOf.format(o?.now ?? new Date())) return time;
  const day = fmt({ weekday: 'short', day: 'numeric', month: 'short' }, 'en-GB').format(at);
  return `${day.replace(',', '')}, ${time}`;
}
